import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  UltimateGenerateRequestArticleTypeEnum,
  UltimateGenerateRequestPromptLanguageEnum,
} from '@models/Sessions/Idea/podcast-api';
import { NgbAccordionModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { PodcastService } from '@services/sessions/podcast.service';
import { finalize, map, of, startWith, Subscription } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { articleTypes } from '../../../../../domain/article';
import { validateIf } from '../../../../../utils/validate-if.validator';
import { languages } from '../../constants';

export enum InputType {
  Topic = 'topic',
  Text = 'text',
  Sources = 'sources',
}

type BranstormConceptFormGroup = FormGroup<{
  concept: FormControl<string>;
  selected: FormControl<boolean>;
  previous: FormControl<boolean>;
}>;

type BranstormConceptsFormArray = FormArray<BranstormConceptFormGroup>;

interface InputForm {
  podcastName: FormControl<string | null>;
  guests: FormControl<number | null>;
  sections: FormControl<number | null>;
  inputType: FormControl<InputType>;
  provideContext: FormControl<boolean>;
  context: FormControl<string | null>;
  topic: FormGroup<{
    name: FormControl<string | null>;
    branstormConcepts: FormArray<BranstormConceptFormGroup>;
  }>;
  text: FormGroup<{
    mainText: FormControl<string | null>;
  }>;
  sources: FormGroup<{
    items: FormArray<FormControl<string | null>>;
    language: FormControl<UltimateGenerateRequestPromptLanguageEnum>;
    style: FormControl<UltimateGenerateRequestArticleTypeEnum>;
    context: FormControl<string | null>;
  }>;
}

const activeTabs = [InputType.Topic, InputType.Text, InputType.Sources];
const allowedInputs = [
  environment.featureFlags.projectCreation.topicInput,
  environment.featureFlags.projectCreation.textInput,
  environment.featureFlags.projectCreation.sourcesInput,
];

@Component({
  selector: 'app-input-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbNavModule,
    NgbAccordionModule,
    TextFieldModule,
  ],
  templateUrl: './input-form.component.html',
  styleUrl: './input-form.component.scss',
})
export class InputFormComponent {
  form!: FormGroup<InputForm>;
  InputType = InputType;
  activeTab = activeTabs[allowedInputs.findIndex(i => !!i)];
  isBrainstorming = false;
  languages = languages;
  maxSourceItems = 5;
  featureFlags = environment.featureFlags.projectCreation;
  hidePodcastTitle = input(false);
  onlyOneTab = input(false);

  get mappedBrainstormConcepts() {
    return (
      this.form.get('topic.branstormConcepts') as BranstormConceptsFormArray
    ).controls.map((control, index) => ({ control, index }));
  }

  get currentBrainstormConcepts() {
    return this.mappedBrainstormConcepts.filter(i => !i.control.value.previous);
  }

  get previousBrainstormConcepts() {
    return this.mappedBrainstormConcepts.filter(i => i.control.value.previous);
  }

  get contentStylesOptions() {
    const lang = this.form.get('sources.language');

    return Object.values(UltimateGenerateRequestArticleTypeEnum).map(value => ({
      label: articleTypes[value][lang.value],
      value,
    }));
  }

  private formBuilder = inject(FormBuilder);
  private podcastService = inject(PodcastService);
  private destroyRef = inject(DestroyRef);
  private regenerateReq: Subscription;

  constructor() {
    this.createForm();
    this.listenFormEvents();
  }

  regenerateBrainstormConcepts() {
    this.regenerateReq = this.brainstormConcepts(true).subscribe(
      branstormConcepts => this.setBrainstormConcepts(branstormConcepts, false),
    );
  }

  onActiveTabChange(tab: InputType) {
    this.activeTab = tab;
    this.form.get('inputType').patchValue(tab);
  }

  addSource() {
    const itemsArray = this.form.get('sources.items') as FormArray;

    if (itemsArray.length === this.maxSourceItems) {
      return;
    }

    itemsArray.push(this.formBuilder.control<string>(null));
  }

  removeSource(idx: number) {
    const itemsArray = this.form.get('sources.items') as FormArray;

    if (itemsArray.length === 1) {
      return;
    }

    itemsArray.removeAt(idx);
  }

  private createForm() {
    this.form = this.formBuilder.group({
      podcastName: this.formBuilder.control<string>('', {
        validators: this.requiredIfInputType([InputType.Topic, InputType.Text]),
      }),
      guests: this.formBuilder.control(1, [Validators.required]),
      sections: this.formBuilder.control(6, [Validators.required]),
      inputType: this.formBuilder.control(this.activeTab, {
        nonNullable: true,
      }),
      provideContext: this.formBuilder.control(false),
      context: this.formBuilder.control<string>(null),
      topic: this.formBuilder.group({
        name: this.formBuilder.control<string>(null, {
          validators: this.requiredIfInputType(InputType.Topic),
        }),
        branstormConcepts: this.formBuilder.array(
          [] as BranstormConceptFormGroup[],
        ),
      }),
      text: this.formBuilder.group({
        mainText: this.formBuilder.control<string>(null, {
          validators: this.requiredIfInputType(InputType.Text),
        }),
      }),
      sources: this.formBuilder.group({
        items: this.formBuilder.array(
          [this.formBuilder.control<string>(null)],
          {
            validators: validateIf(
              () =>
                this.form.get('inputType')!.valueChanges.pipe(
                  takeUntilDestroyed(this.destroyRef),
                  startWith(this.form.get('inputType')!.value),
                  map(v => v === InputType.Sources),
                ),
              [
                (array: FormArray) =>
                  array.controls.some(c => c.value) ? null : { required: true },
              ],
            ),
          },
        ),
        language:
          this.formBuilder.control<UltimateGenerateRequestPromptLanguageEnum>(
            'de',
          ),
        style:
          this.formBuilder.control<UltimateGenerateRequestArticleTypeEnum>(
            'news_report',
          ),
        context: this.formBuilder.control<string | null>(null),
      }),
    });
  }

  private requiredIfInputType = (inputType: InputType | InputType[]) => {
    const inputTypes = Array.isArray(inputType) ? inputType : [inputType];
    return validateIf(() => {
      const currentInputType = this.form.get('inputType');
      return currentInputType.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(currentInputType.value),
        map(inputType => inputTypes.includes(inputType)),
      );
    }, [Validators.required]);
  };

  private listenFormEvents() {
    this.form
      .get('topic.name')
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!this.isBrainstorming) {
          this.setBrainstormConcepts([], true);
        }
      });
  }

  onBrainstormConcepts() {
    this.brainstormConcepts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(branstormConcepts =>
        this.setBrainstormConcepts(branstormConcepts),
      );
  }

  brainstormConcepts(isRegenerate = false) {
    const {
      provideContext,
      context,
      topic: { name, branstormConcepts },
    } = this.form.getRawValue();

    this.stopRegeneratingBrainstormConcepts();

    if (!name) {
      return of([]);
    }

    this.isBrainstorming = true;

    return this.podcastService
      .brainstormConcepts({
        topic: name,
        context: provideContext ? context : '',
        alreadyGeneratedConcepts: isRegenerate
          ? branstormConcepts.map(v => v.concept)
          : undefined,
      })
      .pipe(finalize(() => (this.isBrainstorming = false)));
  }

  private setBrainstormConcepts(concepts: string[], shouldClear = true) {
    const branstormConceptsFormArray = this.form.get(
      'topic.branstormConcepts',
    ) as BranstormConceptsFormArray;

    if (shouldClear) {
      branstormConceptsFormArray.clear();
    } else {
      for (const control of branstormConceptsFormArray.controls) {
        control.patchValue({ selected: false, previous: true });
      }
    }

    for (const concept of concepts) {
      branstormConceptsFormArray.push(
        this.formBuilder.group({
          concept: this.formBuilder.control(concept),
          selected: this.formBuilder.control(false),
          previous: this.formBuilder.control(false),
        }),
      );
    }
  }

  private stopRegeneratingBrainstormConcepts() {
    if (this.regenerateReq) {
      this.regenerateReq.unsubscribe();
      this.regenerateReq = null;
      this.isBrainstorming = false;
    }
  }
}
